import Logo from '$/public/logo/logo-100.svg'
import { BaseLink, Container } from '@/components/Core'
import Button from '@/components/Ui/Button'
import LayoutFragment from '@/lib/fragments/layout'
import { useStoreNavigation, useStorePageElements } from '@/store'
import { Lib } from '@/types'
import { gsap } from 'gsap'
import { useLocale } from 'next-intl'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import { memo, useEffect } from 'react'

const HeaderBg = dynamic(() => import('./HeaderBg'), {
  ssr: false
})

export interface NavProps {
  data: Lib.FragmentOf<typeof LayoutFragment> | null
}

const Nav = ({ data }: NavProps) => {
  //refs
  const pathname = usePathname()
  const locale = useLocale()

  //Stores
  const navOpen = useStoreNavigation.use.navOpen()
  const setNavOpen = useStoreNavigation.use.setNavOpen()
  const headerRef = useStorePageElements.use.headerRef()

  const bgHeaderH = (addition = 0) => {
    if (!headerRef?.current) return
    const h = headerRef.current.offsetHeight
    gsap.to(headerRef.current, {
      '--bg-height': `${h + addition}px`,
      duration: 0.3
    })
  }

  useEffect(() => {
    bgHeaderH()
    /* eslint-disable-next-line */
  }, [headerRef])

  if (!data) return null
  return (
    <>
      <Container
        className={
          'py-md gap-sm 2xl:gap-md flex items-center max-2xl:!text-[16px] max-xl:!text-[14.5px] max-lg:justify-between lg:grid lg:grid-cols-[14vw_1fr_auto]'
        }
      >
        <BaseLink
          className={'h-auto w-full max-w-[10rem]'}
          href={pathname !== `/${locale}` ? '/' : ''}
          transitionType={pathname !== `/${locale}` ? 'slide' : undefined}
          aria-label={'Go to homepage'}
        >
          <Logo />
        </BaseLink>
        <div className="lg:hidden">
          <Button
            as="button"
            type={'button'}
            isNext={false}
            variant="outline"
            color="invert"
            className="cursor-pointer"
            onClick={() => setNavOpen(!navOpen)}
          >
            {navOpen ? 'Close' : 'Menu'}
          </Button>
        </div>
        <div
          className={
            'gap-sm 2xl:gap-md hidden flex-1 items-center justify-center lg:flex'
          }
        >
          {data.navigation.map((item) => {
            let isActive =
              item?.firstLevel?.url && 'slug' in item.firstLevel.url
                ? pathname.split('/').includes(item.firstLevel.url.slug)
                : false

            return (
              <div
                className={'group relative'}
                onMouseEnter={(e) => {
                  const dropdown = e.currentTarget.querySelector('div')
                  const dropdownH = dropdown?.offsetHeight || 0

                  bgHeaderH(dropdownH)
                }}
                onMouseLeave={() => bgHeaderH()}
                key={item.id}
              >
                <Button
                  data={item.firstLevel}
                  variant="ghost"
                  className="relative z-5 max-2xl:!px-3 max-xl:!px-2"
                  color="invert"
                  activeState={isActive}
                />

                {item?.dropdownItems?.length > 0 && (
                  <div className="pointer-events-none absolute top-0 left-0 mt-10 flex translate-y-1 flex-col items-start opacity-0 transition duration-300 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 focus-within:pointer-events-auto focus-within:translate-y-0 focus-within:opacity-100">
                    {item.dropdownItems.map((subItem) => {
                      return (
                        <Button
                          key={subItem?.url?.id}
                          data={subItem}
                          variant="ghost"
                          color="invert"
                          className="mt-sm"
                        />
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <div
          className={
            'gap-sm 2xl:gap-md pointer-events-auto hidden items-center justify-end lg:flex'
          }
        >
          {data.storeLink && (
            <Button
              data={data.storeLink}
              color="invert"
              className="max-2xl:!px-3 max-xl:!px-2"
              variant="outline"
            />
          )}
          {data.h24Link && (
            <Button
              data={data.h24Link}
              icon="ic:baseline-local-phone"
              className="max-2xl:!px-3 max-xl:!px-2"
              color="invert"
              iconPosition="left"
            />
          )}
        </div>
      </Container>

      <HeaderBg />
    </>
  )
}

export default memo(Nav)
